import { useState } from 'react';
import { Drawer } from 'antd';
import { SafetyCertificateOutlined, SearchOutlined } from '@ant-design/icons';

import { Button, Checkbox, Empty, Input, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { soVN } from '../../../lib/dinh-dang';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useDanhSachVaiTro } from '../hooks/useVaiTro';
import { useGanVaiTroNguoiDung, useVaiTroCuaNguoi } from '../hooks/useVaiTroNguoiDung';
import { batTat, khacNhau, locVaiTro } from '../nghiep-vu/vai-tro';
import type { Role, UserRolesCoPhienBan } from '../types';

export interface NguoiDuocGan {
  user_id: string;
  display_name: string;
}

/**
 * Ngăn gán vai trò lớp 2 cho một người (`GET`/`PUT /api/v1/users/{id}/roles`,
 * ADR-0004).
 *
 * Đọc tập vai trò kèm `ETag`, lưu bằng `If-Match`. `412` (người khác vừa đổi)
 * thì báo ngay trong ngăn, tải lại bản mới nhất và dựng lại lựa chọn theo bản
 * đó — người dùng xem lại rồi lưu lần nữa; không ghi đè thầm lặng.
 *
 * Phía gọi **không** mở ngăn cho chính người đang đăng nhập: ADR-0004 cấm tự
 * đổi vai trò. Ngoại lệ "chỉ gỡ bớt" của ADR-0004 không làm ở giao diện.
 */
export function NganGanVaiTro({
  nguoi,
  open,
  onClose,
}: {
  nguoi: NguoiDuocGan;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const hienCo = useVaiTroCuaNguoi(open ? nguoi.user_id : null);
  const dsVaiTro = useDanhSachVaiTro();
  const gan = useGanVaiTroNguoiDung();
  const [xungDot, setXungDot] = useState(false);

  const dangTai = hienCo.isLoading || dsVaiTro.isLoading;
  const loiTai = (!hienCo.data && hienCo.error) || (!dsVaiTro.data && dsVaiTro.error);

  const thuLai = () => {
    if (hienCo.error) void hienCo.refetch();
    if (dsVaiTro.error) void dsVaiTro.refetch();
  };

  const luu = (roleIds: string[], ban: UserRolesCoPhienBan) =>
    gan.mutate(
      { userId: nguoi.user_id, ten: nguoi.display_name, roleIds, etag: ban.etag },
      {
        onSuccess: () => {
          setXungDot(false);
          onClose();
        },
        onError: (err) => setXungDot(err.status === 412),
      },
    );

  let than;
  if (dangTai) {
    than = (
      <div className="space-y-3" aria-busy="true" aria-label={t('vaiTroLop2.dangTaiVaiTro')}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 w-4" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (loiTai) {
    than = (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default px-4 py-3">
        <span className="min-w-0 text-sm text-danger-700">
          {t('vaiTroLop2.loiTaiVaiTroNguoi', { loi: dichLoi(loiTai) })}
        </span>
        <Button variant="outline" size="sm" onClick={thuLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  } else if ((dsVaiTro.data ?? []).length === 0) {
    than = (
      <Empty
        icon={SafetyCertificateOutlined}
        title={t('vaiTroLop2.ganRong')}
        description={t('vaiTroLop2.ganRongMo')}
        action={
          <Button variant="primary" size="md" onClick={() => dieuHuong('/he-thong/vai-tro')}>
            {t('vaiTroLop2.denCauHinhVaiTro')}
          </Button>
        }
      />
    );
  } else if (hienCo.data) {
    // Dựng lại theo `ETag`: tải lại sau `412` là lựa chọn về đúng bản mới nhất.
    than = (
      <ChonVaiTro
        key={hienCo.data.etag}
        ban={hienCo.data}
        dsVaiTro={dsVaiTro.data ?? []}
        dangLuu={gan.isPending}
        loi={xungDot ? null : gan.error}
        onLuu={luu}
        onHuy={onClose}
      />
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('vaiTroLop2.ganTieuDe', { ten: nguoi.display_name })}
      placement="right"
      size={440}
      destroyOnHidden
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-content-secondary">{t('vaiTroLop2.ganMo')}</p>
        {xungDot && (
          <div
            role="alert"
            className="rounded border border-warning-100 bg-warning-50 px-3 py-2.5 text-xs leading-relaxed text-warning-700"
          >
            {t('vaiTroLop2.ganXungDot', { ten: nguoi.display_name })}
          </div>
        )}
        {than}
      </div>
    </Drawer>
  );
}

/** Danh sách hộp kiểm và nút lưu — dựng lại sạch theo từng `ETag`. */
function ChonVaiTro({
  ban,
  dsVaiTro,
  dangLuu,
  loi,
  onLuu,
  onHuy,
}: {
  ban: UserRolesCoPhienBan;
  dsVaiTro: Role[];
  dangLuu: boolean;
  loi: ApiError | null;
  onLuu: (roleIds: string[], ban: UserRolesCoPhienBan) => void;
  onHuy: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  // Chỉ giữ vai trò còn trong danh sách: vai trò vừa bị xoá không gửi lại được (`422`).
  const conCo = new Set(dsVaiTro.map((r) => r.id));
  const banDau = ban.role_ids.filter((id) => conCo.has(id));
  const [daChon, setDaChon] = useState<Set<string>>(() => new Set(banDau));
  const seGui = dsVaiTro.map((r) => r.id).filter((id) => daChon.has(id));
  const coThayDoi = khacNhau(seGui, ban.role_ids);
  // Lọc chỉ để tìm cho nhanh: vai trò đã chọn mà bị lọc khuất vẫn được gửi.
  const [tuKhoa, setTuKhoa] = useState('');
  const hien = locVaiTro(dsVaiTro, tuKhoa);

  return (
    <div className="space-y-4">
      <Input
        allowClear
        prefix={<SearchOutlined className="text-content-muted" />}
        placeholder={t('vaiTroLop2.timPlaceholder')}
        aria-label={t('vaiTroLop2.timNhan')}
        value={tuKhoa}
        onChange={(e) => setTuKhoa(e.target.value)}
      />
      {hien.length === 0 ? (
        // Rỗng vì tìm: không mô tả, không nút hành động.
        <Empty
          className="rounded-lg border border-dashed border-default py-8"
          title={t('vaiTroLop2.rongLoc', { tuKhoa: tuKhoa.trim() })}
        />
      ) : (
        <fieldset className="min-w-0">
          <legend className="sr-only">{t('vaiTroLop2.ganLegend')}</legend>
          <ul className="divide-y divide-default rounded-lg border border-default">
            {hien.map((r) => (
              <li key={r.id} className="px-3 py-2.5">
                <Checkbox
                  checked={daChon.has(r.id)}
                  disabled={dangLuu}
                  onChange={() => setDaChon(batTat(daChon, r.id))}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm text-content-primary">{r.name}</span>
                    <span className="flex flex-wrap items-baseline gap-x-2 text-2xs text-content-muted">
                      <span className="break-all font-mono">{r.code}</span>
                      <span>
                        {r.permissions.length > 0
                          ? t('vaiTroLop2.soQuyen', { so: soVN(r.permissions.length, locale) })
                          : t('vaiTroLop2.chuaCoQuyen')}
                      </span>
                    </span>
                  </span>
                </Checkbox>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {loi && <LoiTrongHop>{dichLoi(loi)}</LoiTrongHop>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-content-muted" role="status">
          {t('vaiTroLop2.daChonSoVaiTro', { so: seGui.length })}
        </span>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="md" onClick={onHuy}>
            {t('chung.huy')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={dangLuu}
            disabled={!coThayDoi}
            onClick={() => onLuu(seGui, ban)}
          >
            {dangLuu ? t('chung.dangLuu') : t('vaiTroLop2.luuVaiTro')}
          </Button>
        </div>
      </div>
    </div>
  );
}
